import { Component } from '@theme/component';

/**
 * @typedef {Object} TabsRefs
 * @property {HTMLElement[]} tabs
 * @property {HTMLElement[]} panels
 * @property {HTMLElement} indicator
 */

/** @extends {Component<TabsRefs>} */
export class TabsComponent extends Component {
  #active = 0;
  /** @type {AbortController | null} */
  #abort = null;

  connectedCallback() {
    super.connectedCallback();
    const { tabs, panels } = this.refs;
    if (!tabs?.length) return;

    this.#syncLabels();

    this.#active = Math.max(0, tabs.findIndex((t) => t.getAttribute('aria-selected') === 'true'));

    panels?.forEach((panel, i) => {
      if (i !== this.#active) panel.setAttribute('inert', '');
    });

    this.#setupEvents();
    requestAnimationFrame(() => this.#updateIndicator());
  }

  /**
   * The tab label lives on the block's own settings, but the block only
   * renders its canonical (shopify_attributes-carrying) markup as the
   * tabpanel — not the nav button. Read the label back off each panel's
   * data-tab-label so the button text stays correct even when the editor
   * only patches the panel.
   */
  #syncLabels() {
    const { tabs, panels } = this.refs;
    if (!tabs?.length || !panels?.length) return;

    tabs.forEach((tab, i) => {
      const label = panels[i]?.dataset.tabLabel;
      if (label) tab.textContent = label;
    });
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#abort?.abort();
  }

  #setupEvents() {
    this.#abort = new AbortController();
    const opts = { signal: this.#abort.signal };

    this.refs.tabs.forEach((tab, i) => {
      tab.addEventListener('click', () => this.#activateTab(i), opts);
    });

    this.addEventListener('keydown', (e) => this.#handleKeydown(e), opts);
  }

  #activateTab(index) {
    const { tabs, panels } = this.refs;
    if (!tabs?.length || index === this.#active) return;

    tabs[this.#active]?.setAttribute('aria-selected', 'false');
    tabs[this.#active]?.setAttribute('tabindex', '-1');
    panels?.[this.#active]?.setAttribute('inert', '');

    this.#active = index;
    tabs[this.#active]?.setAttribute('aria-selected', 'true');
    tabs[this.#active]?.setAttribute('tabindex', '0');
    panels?.[this.#active]?.removeAttribute('inert');

    this.#updateIndicator();
  }

  #updateIndicator() {
    const { indicator, tabs } = this.refs;
    if (!indicator || !tabs?.[this.#active]) return;
    const tab = tabs[this.#active];
    indicator.style.width = `${tab.offsetWidth}px`;
    indicator.style.transform = `translateX(${tab.offsetLeft}px)`;
  }

  /** @param {KeyboardEvent} event */
  #handleKeydown(event) {
    const { tabs } = this.refs;
    if (!tabs?.length) return;

    const map = { ArrowLeft: -1, ArrowRight: 1 };
    let next;

    if (event.key === 'Home') next = 0;
    else if (event.key === 'End') next = tabs.length - 1;
    else if (event.key in map) next = (this.#active + map[event.key] + tabs.length) % tabs.length;
    else return;

    event.preventDefault();
    this.#activateTab(next);
    tabs[next]?.focus();
  }
}

customElements.define('tabs-component', TabsComponent);
